import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { store, RootState } from './store';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ToastProvider } from './components/common';
import { LoginPage, SignupPage } from './pages/auth';
import { HomePage } from './pages/HomePage';
import { BooksListPage, BookDetailPage, BookReaderPage, ProfilePage } from './pages/consumer';
import {
  DashboardPage,
  ManageBooksPage,
  AddBookPage,
  EditBookPage,
  DeletedBooksPage,
  ManageUsersPage,
  AddAdminPage,
  ReadingStatsPage,
  UserDetailPage,
} from './pages/admin';
import { NotFoundPage } from './pages/NotFoundPage';
import { UserRole } from './types';

// Component to handle home route based on auth state
const HomeRoute: React.FC = () => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  if (!isAuthenticated) {
    return <HomePage />;
  }

  // Redirect authenticated users to appropriate dashboard
  if (user?.role === UserRole.ADMIN) {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/books" replace />;
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <ToastProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomeRoute />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />

          {/* Consumer Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="books" element={<BooksListPage />} />
            <Route path="books/:id" element={<BookDetailPage />} />
            <Route path="books/:id/read" element={<BookReaderPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="books" element={<ManageBooksPage />} />
            <Route path="books/add" element={<AddBookPage />} />
            <Route path="books/edit/:id" element={<EditBookPage />} />
            <Route path="deleted-books" element={<DeletedBooksPage />} />
            <Route path="reading-stats" element={<ReadingStatsPage />} />
            <Route path="users" element={<ManageUsersPage />} />
            <Route path="users/add-admin" element={<AddAdminPage />} />
            <Route path="users/:id" element={<UserDetailPage />} />
            <Route path="profile" element={<ProfilePage />} />
          </Route>

          {/* 404 Page */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
      </ToastProvider>
    </Provider>
  );
};

export default App;
